import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { PushDemPageNotificationInput } from '../hooks/useDemPageNotifications';
import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';

export type DemPersistedNotification = {
  id: string;
  kind: string;
  title: string;
  body: string;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

function toneForDeliveryTitle(title: string): PushDemPageNotificationInput['tone'] {
  const t = title.toLowerCase();
  if (t.includes('cancel') || t.includes('fail') || t.includes('reject') || t.includes('expir')) {
    return 'warn';
  }
  if (t.includes('completed') || t.includes('picked')) return 'ok';
  return 'info';
}

export async function fetchDemDeliveryNotifications(userId: string): Promise<DemPersistedNotification[]> {
  const db = getDbSupabase('crm');
  const { data, error } = await db
    .from('crm_notifications')
    .select('id, kind, title, body, link, read_at, created_at')
    .eq('user_id', userId)
    .eq('kind', 'delivery_status')
    .is('read_at', null)
    .order('created_at', { ascending: false })
    .limit(40);
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []) as DemPersistedNotification[];
}

export async function markDemDeliveryNotificationRead(id: string): Promise<void> {
  const db = getDbSupabase('crm');
  const { error } = await db
    .from('crm_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id)
    .is('read_at', null);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function markAllDemDeliveryNotificationsRead(userId: string): Promise<void> {
  const db = getDbSupabase('crm');
  const { error } = await db
    .from('crm_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('kind', 'delivery_status')
    .is('read_at', null);
  if (error) throw new Error(formatSupabaseError(error));
}

/** Load delivery status notifications into the DEM bell + toast on new inserts. */
export function useDemDeliveryStatusNotifications(push: (input: PushDemPageNotificationInput) => void): {
  persisted: DemPersistedNotification[];
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
} {
  const { profile, session } = useAuth();
  // RLS is auth.uid() — prefer session user id so updates/clears actually match rows.
  const userId = session?.user?.id || profile?.id || '';
  const seenIdsRef = useRef<Set<string>>(new Set());
  const [persisted, setPersisted] = useState<DemPersistedNotification[]>([]);

  const refresh = useCallback(async () => {
    if (!userId) {
      setPersisted([]);
      return;
    }
    try {
      const rows = await fetchDemDeliveryNotifications(userId);
      setPersisted(rows);
      for (const row of rows) seenIdsRef.current.add(row.id);
    } catch {
      /* keep prior list */
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Fallback if Realtime misses an insert (common until publication is fixed).
  useEffect(() => {
    if (!userId) return;
    const timer = window.setInterval(() => {
      void (async () => {
        try {
          const rows = await fetchDemDeliveryNotifications(userId);
          setPersisted((prev) => {
            const known = new Set(prev.map((r) => r.id));
            const fresh = rows.filter((r) => !known.has(r.id) && !seenIdsRef.current.has(r.id));
            if (fresh.length === 0) {
              // Drop any that were marked read elsewhere / cleared.
              const unreadIds = new Set(rows.map((r) => r.id));
              return prev.filter((r) => unreadIds.has(r.id));
            }
            for (const row of fresh) {
              seenIdsRef.current.add(row.id);
              if (!row.read_at) {
                push({
                  tone: toneForDeliveryTitle(row.title),
                  title: row.title,
                  lines: row.body ? [row.body] : [],
                  durationMs: 8000,
                });
              }
            }
            const unreadIds = new Set(rows.map((r) => r.id));
            const kept = prev.filter((r) => unreadIds.has(r.id));
            return [...fresh, ...kept].slice(0, 40);
          });
        } catch {
          /* ignore */
        }
      })();
    }, 25_000);
    return () => window.clearInterval(timer);
  }, [userId, push]);

  useEffect(() => {
    if (!userId) return;
    const db = getDbSupabase('crm');
    const channel = db
      .channel(`dem-delivery-notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'crm',
          table: 'crm_notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as DemPersistedNotification;
          if (!row?.id || row.kind !== 'delivery_status') return;
          if (row.read_at) return;
          if (seenIdsRef.current.has(row.id)) return;
          seenIdsRef.current.add(row.id);
          setPersisted((prev) => [row, ...prev.filter((r) => r.id !== row.id)].slice(0, 40));
          push({
            tone: toneForDeliveryTitle(row.title),
            title: row.title,
            lines: row.body ? [row.body] : [],
            durationMs: 8000,
          });
        },
      )
      .subscribe();

    return () => {
      void db.removeChannel(channel);
    };
  }, [userId, push]);

  const markRead = useCallback(async (id: string) => {
    setPersisted((prev) => prev.filter((row) => row.id !== id));
    try {
      await markDemDeliveryNotificationRead(id);
    } catch {
      void refresh();
    }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    setPersisted([]);
    if (!userId) return;
    try {
      await markAllDemDeliveryNotificationsRead(userId);
    } catch {
      void refresh();
    }
  }, [userId, refresh]);

  return { persisted, refresh, markRead, markAllRead };
}
